import React from 'react';
import { Table, Typography, Tag, Space } from 'antd';
import {
  PlusOutlined,
  ExperimentOutlined,
  MedicineBoxOutlined,
  AppstoreAddOutlined,
  FallOutlined,
  FormOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  CarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

export const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  try {
    return new Intl.DateTimeFormat('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(dateStr));
  } catch (e) {
    console.log('Error al formatear fecha', dateStr, e);
    return dateStr.split('T')[0];
  }
};

export const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return '-';
  return dayjs(dateStr).format('DD/MM/YYYY hh:mm A');
};

export const formatCurrency = (val: number): string =>
  `$ ${new Intl.NumberFormat('es-CO').format(val || 0)}`;

export const renderInputsSummary = (pageData: readonly any[]) => {
  let subtotal = 0;
  let totalImpuestos = 0;
  pageData.forEach(({ quantity, unit_price, tax_amount }: any) => {
    subtotal += (quantity || 0) * (unit_price || 0);
    totalImpuestos += tax_amount || 0;
  });
  const totalGeneral = subtotal + totalImpuestos;
  if (totalGeneral <= 0) return null;
  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={3} align="right">
        <div style={{ textAlign: 'right' }}>
          {totalImpuestos > 0 && (
            <div style={{ fontSize: 11, color: '#8c8c8c', fontWeight: 'normal' }}>
              Subtotal: ${new Intl.NumberFormat('es-CO').format(subtotal)} | IVA: ${new Intl.NumberFormat('es-CO').format(totalImpuestos)}
            </div>
          )}
          <Text type="secondary" strong>Total General:</Text>
        </div>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1}>
        <Text strong style={{ color: totalImpuestos > 0 ? '#fa8c16' : '#1890ff' }}>
          $ {new Intl.NumberFormat('es-CO').format(totalGeneral)}
        </Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={2}></Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

export const renderOutputsSummary = (pageData: readonly any[]) => {
  let totalValor = 0;
  pageData.forEach(({ quantity, unit_price }: any) => {
    totalValor += (quantity || 0) * (unit_price || 0);
  });
  if (totalValor <= 0) return null;
  return (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0} colSpan={3} align="right">
        <Text type="secondary" strong>Total General Est.:</Text>
      </Table.Summary.Cell>
      <Table.Summary.Cell index={1}>
        <Text strong style={{ color: '#52c41a' }}>
          $ {new Intl.NumberFormat('es-CO').format(totalValor)}
        </Text>
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
};

export const renderTotalCol = (_: any, record: any) => {
  const base = (record.quantity || 0) * (record.unit_price || 0);
  const tax = record.tax_amount || 0;
  const t = base + tax;
  if (t <= 0) return '-';
  if (tax > 0) {
    return (
      <div>
        <span style={{ fontWeight: 600, color: '#fa8c16' }}>
          ${new Intl.NumberFormat('es-CO').format(t)}
        </span>
        <div style={{ fontSize: 10, color: '#8c8c8c' }}>
          (IVA: +${new Intl.NumberFormat('es-CO').format(tax)})
        </div>
      </div>
    );
  }
  return `$${new Intl.NumberFormat('es-CO').format(t)}`;
};

export const getResolvedElementInfo = (input: any, itemsMap: Map<string, any>) => {
  const elementId = input?.element_id;
  const storageEl = input?.storage_elements;
  const catalogItem =
    (elementId ? itemsMap.get(String(elementId)) : null) ||
    (storageEl?.id ? itemsMap.get(String(storageEl.id)) : null) ||
    (storageEl?.uid ? itemsMap.get(String(storageEl.uid)) : null);

  const isService =
    catalogItem?.register_type === 'SERVICE' ||
    storageEl?.register_type === 'SERVICE' ||
    input?.register_type === 'SERVICE' ||
    (storageEl?.location === 'N/A' && !catalogItem);

  const name = catalogItem?.name || storageEl?.name || 'Insumo';
  const measureUnit = catalogItem?.measure_unit || storageEl?.measure_unit || '';
  const code =
    catalogItem?.custom_id ||
    catalogItem?.sku ||
    storageEl?.custom_id ||
    storageEl?.sku ||
    '';

  return {
    catalogItem,
    isService,
    registerType: isService ? 'SERVICE' : 'PRODUCT',
    name,
    measureUnit,
    code,
  };
};

export const getLogIcon = (type: string) => {
  switch (type) {
    case 'OBSERVATION':
      return <FormOutlined style={{ color: '#1890ff' }} />;
    case 'INPUT_APPLICATION':
      return <MedicineBoxOutlined style={{ color: '#eb2f96' }} />;
    case 'LABOR_LOG':
      return <TeamOutlined style={{ color: '#722ed1' }} />;
    case 'SERVICE_EXPENSE':
      return <CarOutlined style={{ color: '#13c2c2' }} />;
    case 'SUPPLY_PURCHASE':
      return <ShoppingCartOutlined style={{ color: '#fa8c16' }} />;
    case 'OUTPUT_HARVEST':
      return <AppstoreAddOutlined style={{ color: '#52c41a' }} />;
    case 'MORTALITY_LOSS':
      return <FallOutlined style={{ color: '#f5222d' }} />;
    case 'UNIT_ADJUSTMENT':
      return <ExperimentOutlined style={{ color: '#faad14' }} />;
    default:
      return <PlusOutlined />;
  }
};

export const getLogColor = (type: string) => {
  switch (type) {
    case 'OBSERVATION':
      return '#1890ff';
    case 'INPUT_APPLICATION':
      return '#eb2f96';
    case 'LABOR_LOG':
      return '#722ed1';
    case 'SERVICE_EXPENSE':
      return '#13c2c2';
    case 'SUPPLY_PURCHASE':
      return '#fa8c16';
    case 'OUTPUT_HARVEST':
      return '#52c41a';
    case 'MORTALITY_LOSS':
      return '#f5222d';
    case 'UNIT_ADJUSTMENT':
      return '#faad14';
    default:
      return 'gray';
  }
};

export const getStatusLabel = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Activo';
    case 'PAUSED':
      return 'Pausado';
    case 'CLOSED':
      return 'Cerrado';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return status;
  }
};

export const getStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'blue';
    case 'PAUSED':
      return 'orange';
    case 'CLOSED':
      return 'green';
    case 'CANCELLED':
      return 'red';
    default:
      return 'gray';
  }
};

export const getLogTypeLabel = (type: string) => {
  switch (type) {
    case 'OBSERVATION':
      return 'Observación';
    case 'INPUT_APPLICATION':
      return 'Consumo de Insumo';
    case 'LABOR_LOG':
      return 'Jornal / Mano de Obra';
    case 'SERVICE_EXPENSE':
      return 'Gasto / Servicio Externo';
    case 'SUPPLY_PURCHASE':
      return 'Compra de Insumos';
    case 'OUTPUT_HARVEST':
      return 'Recolección/Cosecha/Producción';
    case 'MORTALITY_LOSS':
      return 'Mortalidad/Pérdida de Capacidad';
    case 'UNIT_ADJUSTMENT':
      return 'Ajuste de Lote/Capacidad';
    default:
      return type;
  }
};

export const getProductiveUnitTypeLabel = (type: string) => {
  switch (type) {
    case 'BATCH':
      return 'Lote / Batch de Fabricación';
    case 'HERD':
      return 'Hato / Lote / Rebaño (Grupal)';
    case 'ANIMAL':
      return 'Agropecuario (Animal individual)';
    case 'CROP':
      return 'Agrícola (Cultivo / Siembra)';
    case 'EQUIPMENT':
      return 'Equipo / Línea de Proceso';
    case 'CUSTOM':
      return 'Personalizado';
    default:
      return type;
  }
};

export const getTagRecord = (record: any, selectedLog?: any) => {
  if (record.contacts?.name) return <Tag icon={<UserOutlined />}>{record.contacts.name}</Tag>;
  if (record.parent_log?.contacts?.name) return <Tag icon={<UserOutlined />}>{record.parent_log.contacts.name}</Tag>;
  if (selectedLog?.contacts?.name) return <Tag icon={<UserOutlined />}>{selectedLog.contacts.name}</Tag>;
  if (record.contact_id) return <Tag icon={<UserOutlined />}>Asignado</Tag>;
  return '-';
};

export interface AggregatedCycleData {
  allInputsRaw: any[];
  purchasedInputs: any[];
  totalPurchasedBase: number;
  totalPurchasedTaxes: number;
  totalPurchasedCost: number;
  purchasesSummaryData: any[];
  appliedMaterialInputs: any[];
  totalMaterialCost: number;
  appliedLaborInputs: any[];
  totalLaborCost: number;
  appliedExpenseInputs: any[];
  totalExpenseCost: number;
  totalOperationalCost: number;
  appliedInputs: any[];
  appliedSummaryData: any[];
  allOutputs: any[];
  totalOutputValue: number;
  totalOutputQuantity: number;
  outputsSummaryData: any[];
  balanceEstimado: number;
}

export const aggregateCycleData = (
  logs: any[],
  itemsMap: Map<string, any>
): AggregatedCycleData => {
  const allInputsRaw = logs.flatMap((log: any) =>
    (log.production_cycle_log_inputs || []).map((input: any) => {
      const info = getResolvedElementInfo(input, itemsMap);
      return {
        ...input,
        log_date: log.log_date,
        stage_name: log.production_cycle_stages?.name,
        log_notes: log.notes,
        parent_log: log,
        tax_amount: input.tax_amount || 0,
        total_cost: (input.quantity || 0) * (input.unit_price || 0),
        total_cost_with_tax:
          (input.quantity || 0) * (input.unit_price || 0) + (input.tax_amount || 0),
        resolved_is_service: info.isService,
        resolved_register_type: info.registerType,
        resolved_name: info.name,
        resolved_measure_unit: info.measureUnit,
        resolved_code: info.code,
      };
    })
  );

  const purchasedInputs = allInputsRaw.filter(
    (i: any) => i.parent_log?.log_type === 'SUPPLY_PURCHASE'
  );
  const totalPurchasedBase = purchasedInputs.reduce(
    (sum: number, item: any) => sum + (item.total_cost || 0),
    0
  );
  const totalPurchasedTaxes = purchasedInputs.reduce(
    (sum: number, item: any) => sum + (item.tax_amount || 0),
    0
  );
  const totalPurchasedCost = totalPurchasedBase + totalPurchasedTaxes;

  const purchasesByElementMap: { [key: string]: any } = {};
  purchasedInputs.forEach((item: any) => {
    const key = item.element_id || item.resolved_name || 'Desconocido';
    if (!purchasesByElementMap[key]) {
      purchasesByElementMap[key] = {
        id: key,
        name: item.resolved_name,
        totalQuantity: 0,
        totalCost: 0,
        totalTaxes: 0,
        totalWithTaxes: 0,
        count: 0,
        measure_unit: item.resolved_measure_unit,
      };
    }
    purchasesByElementMap[key].totalQuantity += item.quantity || 0;
    purchasesByElementMap[key].totalCost += item.total_cost || 0;
    purchasesByElementMap[key].totalTaxes += item.tax_amount || 0;
    purchasesByElementMap[key].totalWithTaxes += item.total_cost_with_tax || 0;
    purchasesByElementMap[key].count += 1;
  });
  const purchasesSummaryData = Object.values(purchasesByElementMap).sort(
    (a, b) => b.totalWithTaxes - a.totalWithTaxes
  );

  const appliedMaterialInputs = allInputsRaw.filter(
    (i: any) =>
      i.parent_log?.log_type === 'INPUT_APPLICATION' && !i.resolved_is_service
  );
  const totalMaterialCost = appliedMaterialInputs.reduce(
    (sum: number, item: any) => sum + (item.total_cost || 0),
    0
  );

  const appliedLaborInputs = allInputsRaw.filter(
    (i: any) =>
      i.parent_log?.log_type === 'LABOR_LOG' ||
      (i.parent_log?.log_type === 'INPUT_APPLICATION' && i.resolved_is_service)
  );
  const totalLaborCost = appliedLaborInputs.reduce(
    (sum: number, item: any) => sum + (item.total_cost || 0),
    0
  );

  const appliedExpenseInputs = allInputsRaw.filter(
    (i: any) => i.parent_log?.log_type === 'SERVICE_EXPENSE'
  );
  const totalExpenseCost = appliedExpenseInputs.reduce(
    (sum: number, item: any) =>
      sum + (item.total_cost_with_tax || item.total_cost || 0),
    0
  );

  const totalOperationalCost =
    totalMaterialCost + totalLaborCost + totalExpenseCost;

  const appliedInputs = [
    ...appliedMaterialInputs,
    ...appliedLaborInputs,
    ...appliedExpenseInputs,
  ];

  const appliedByElementMap: { [key: string]: any } = {};
  appliedInputs.forEach((item: any) => {
    const key = item.element_id || item.resolved_name || 'Desconocido';
    if (!appliedByElementMap[key]) {
      appliedByElementMap[key] = {
        id: key,
        name: item.resolved_name,
        register_type: item.resolved_register_type,
        is_service: item.resolved_is_service,
        log_type: item.parent_log?.log_type,
        totalQuantity: 0,
        totalCost: 0,
        count: 0,
        measure_unit: item.resolved_measure_unit,
      };
    }
    appliedByElementMap[key].totalQuantity += item.quantity || 0;
    appliedByElementMap[key].totalCost +=
      item.total_cost_with_tax || item.total_cost || 0;
    appliedByElementMap[key].count += 1;
  });
  const appliedSummaryData = Object.values(appliedByElementMap).sort(
    (a, b) => b.totalCost - a.totalCost
  );

  const allOutputs = logs.flatMap((log: any) =>
    (log.production_cycle_log_outputs || []).map((output: any) => ({
      ...output,
      log_date: log.log_date,
      stage_name: log.production_cycle_stages?.name,
      log_notes: log.notes,
      parent_log: log,
      total_value: (output.quantity || 0) * (output.unit_price || 0),
    }))
  );

  const totalOutputValue = allOutputs.reduce(
    (sum: number, item: any) => sum + (item.total_value || 0),
    0
  );
  const totalOutputQuantity = allOutputs.reduce(
    (sum: number, item: any) => sum + (item.quantity || 0),
    0
  );

  const outputsByElementMap: { [key: string]: any } = {};
  allOutputs.forEach((item: any) => {
    const key = item.element_id || item.storage_elements?.name || 'Desconocido';
    if (!outputsByElementMap[key]) {
      outputsByElementMap[key] = {
        id: key,
        name: item.storage_elements?.name || 'Sin nombre',
        totalQuantity: 0,
        totalValue: 0,
        count: 0,
      };
    }
    outputsByElementMap[key].totalQuantity += item.quantity || 0;
    outputsByElementMap[key].totalValue += item.total_value || 0;
    outputsByElementMap[key].count += 1;
  });
  const outputsSummaryData = Object.values(outputsByElementMap).sort(
    (a, b) => b.totalValue - a.totalValue
  );

  const balanceEstimado = totalOutputValue - totalOperationalCost;

  return {
    allInputsRaw,
    purchasedInputs,
    totalPurchasedBase,
    totalPurchasedTaxes,
    totalPurchasedCost,
    purchasesSummaryData,
    appliedMaterialInputs,
    totalMaterialCost,
    appliedLaborInputs,
    totalLaborCost,
    appliedExpenseInputs,
    totalExpenseCost,
    totalOperationalCost,
    appliedInputs,
    appliedSummaryData,
    allOutputs,
    totalOutputValue,
    totalOutputQuantity,
    outputsSummaryData,
    balanceEstimado,
  };
};
